// Mental Math (flash anzan) trainer: numbers flash in timed frames and the user
// computes the result in their head, then types the answer for instant feedback.
// Supports +, −, ×, ÷ (and mixed), a custom flash speed, a from/to number range,
// and how many numbers appear on screen at the same time.
import React, { useCallback, useEffect, useRef, useState } from "react";
import AbacusBoard from "./AbacusBoard";
import {
  generateOperationSequence,
  partialResults,
  itemsToExpression,
  numberToRods,
  emptyRods,
  maxValueForRods,
  formatNumber,
} from "./mathUtils";

const SPEED_PRESETS = [
  { label: "Chill", ms: 1500 },
  { label: "Steady", ms: 1000 },
  { label: "Brisk", ms: 700 },
  { label: "Fast", ms: 450 },
  { label: "Lightning", ms: 250 },
];

const OPERATIONS = [
  { id: "add", label: "Add +" },
  { id: "subtract", label: "Subtract −" },
  { id: "multiply", label: "Multiply ×" },
  { id: "divide", label: "Divide ÷" },
  { id: "mixed", label: "Mixed" },
];

export default function MentalMath({ settings, onResult }) {
  const [operation, setOperation] = useState("add");
  const [terms, setTerms] = useState(5);
  const [simultaneous, setSimultaneous] = useState(1);
  const [rangeMin, setRangeMin] = useState(1);
  const [rangeMax, setRangeMax] = useState(9);
  const [speedChoice, setSpeedChoice] = useState(1000); // number ms or "other"
  const [customSeconds, setCustomSeconds] = useState(1);
  const [showBoard, setShowBoard] = useState(true);

  const [phase, setPhase] = useState("idle"); // idle | flashing | answer | result
  const [frame, setFrame] = useState(null); // current frame of items on screen
  const [frameKey, setFrameKey] = useState(0);
  const [runningTotal, setRunningTotal] = useState(0);
  const [problem, setProblem] = useState(null);
  const [guess, setGuess] = useState("");
  const [verdict, setVerdict] = useState(null); // 'right' | 'wrong'
  const [streak, setStreak] = useState(0);

  const timers = useRef([]);
  const inputRef = useRef(null);

  const speedMs = speedChoice === "other"
    ? Math.max(50, Math.round((Number(customSeconds) || 0) * 1000))
    : speedChoice;

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  }, []);
  useEffect(() => () => clearTimers(), [clearTimers]);

  const boardCap = maxValueForRods(settings.rodCount);
  const boardRods = frame != null
    ? numberToRods(Math.min(Math.abs(runningTotal), boardCap), settings.rodCount)
    : emptyRods(settings.rodCount);
  const boardOverflow = frame != null && runningTotal > boardCap;

  function start() {
    clearTimers();
    setVerdict(null);
    setGuess("");
    setRunningTotal(0);

    const p = generateOperationSequence({
      terms,
      min: rangeMin,
      max: rangeMax,
      operation,
    });
    setProblem(p);

    // Group items into frames of `simultaneous`, tracking the running total
    // at the end of each frame for the abacus mirror.
    const partials = partialResults(p.items);
    const perFrame = Math.max(1, Math.min(simultaneous, p.items.length));
    const frames = [];
    for (let i = 0; i < p.items.length; i += perFrame) {
      const slice = p.items.slice(i, i + perFrame);
      frames.push({ items: slice, partialEnd: partials[i + slice.length - 1] });
    }

    setPhase("flashing");
    frames.forEach((fr, i) => {
      timers.current.push(
        setTimeout(() => {
          setFrame(fr);
          setFrameKey((k) => k + 1);
          setRunningTotal(fr.partialEnd);
        }, i * speedMs)
      );
      timers.current.push(
        setTimeout(() => setFrame(null), i * speedMs + speedMs * 0.72)
      );
    });

    timers.current.push(
      setTimeout(() => {
        setFrame(null);
        setPhase("answer");
        setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      }, frames.length * speedMs)
    );
  }

  function check(e) {
    e.preventDefault();
    if (!problem) return;
    const right = Number(guess) === problem.answer;
    setVerdict(right ? "right" : "wrong");
    setPhase("result");
    setStreak((prev) => (right ? prev + 1 : 0));
    if (onResult) onResult(right);
  }

  function reset() {
    clearTimers();
    setPhase("idle");
    setFrame(null);
    setVerdict(null);
    setGuess("");
    setProblem(null);
    setRunningTotal(0);
  }

  const flashing = phase === "flashing";
  const frameFontClass = frame && frame.items.length >= 3 ? "multi-lg"
    : frame && frame.items.length === 2 ? "multi-sm" : "";

  return (
    <div className="abx-mental">
      <div className="abx-panel abx-mental-controls">
        <h3 className="abx-panel-title">Mental Math · Flash Anzan</h3>

        <div className="abx-field">
          <label>Operation</label>
          <div className="abx-chip-row">
            {OPERATIONS.map((op) => (
              <button key={op.id} type="button"
                className={`abx-chip ${operation === op.id ? "is-on" : ""}`}
                disabled={flashing}
                onClick={() => setOperation(op.id)}>
                {op.label}
              </button>
            ))}
          </div>
        </div>

        <div className="abx-field">
          <label>Total numbers: <b>{terms}</b></label>
          <input type="range" min="2" max="30" value={terms}
            disabled={flashing}
            onChange={(e) => {
              const v = Number(e.target.value);
              setTerms(v);
              if (simultaneous > v) setSimultaneous(v);
            }} />
        </div>

        <div className="abx-field">
          <label>Shown on screen at the same time: <b>{simultaneous}</b></label>
          <input type="range" min="1" max={Math.max(1, terms)} value={simultaneous}
            disabled={flashing}
            onChange={(e) => setSimultaneous(Number(e.target.value))} />
        </div>

        <div className="abx-field">
          <label>Number range</label>
          <div className="abx-range-row">
            <span>From</span>
            <input type="number" className="abx-num-input" value={rangeMin}
              disabled={flashing}
              onChange={(e) => setRangeMin(Number(e.target.value))} />
            <span>to</span>
            <input type="number" className="abx-num-input" value={rangeMax}
              disabled={flashing}
              onChange={(e) => setRangeMax(Number(e.target.value))} />
          </div>
        </div>

        <div className="abx-field">
          <label>Flash speed</label>
          <div className="abx-chip-row">
            {SPEED_PRESETS.map((sp) => (
              <button key={sp.ms} type="button"
                className={`abx-chip ${speedChoice === sp.ms ? "is-on" : ""}`}
                disabled={flashing}
                onClick={() => setSpeedChoice(sp.ms)}>
                {sp.label}
              </button>
            ))}
            <button type="button"
              className={`abx-chip ${speedChoice === "other" ? "is-on" : ""}`}
              disabled={flashing}
              onClick={() => setSpeedChoice("other")}>
              Other…
            </button>
          </div>
          {speedChoice === "other" && (
            <div className="abx-range-row">
              <input type="number" className="abx-num-input" min="0.1" step="0.1"
                value={customSeconds}
                disabled={flashing}
                onChange={(e) => setCustomSeconds(e.target.value)} />
              <span>seconds per flash</span>
            </div>
          )}
        </div>

        <label className="abx-switch">
          <input type="checkbox" checked={showBoard}
            onChange={(e) => setShowBoard(e.target.checked)} />
          <span>Mirror running total on abacus</span>
        </label>

        <div className="abx-mental-actions">
          {phase === "idle" || phase === "result" ? (
            <button type="button" className="abx-btn abx-btn--primary" onClick={start}>
              {phase === "result" ? "Play again" : "Start flashing"}
            </button>
          ) : (
            <button type="button" className="abx-btn" onClick={reset}>Stop</button>
          )}
          <div className="abx-streak" title="Correct answers in a row">
            🔥 Streak <b>{streak}</b>
          </div>
        </div>
      </div>

      <div className="abx-panel abx-mental-stage">
        <div className="abx-flash-stage">
          {phase === "idle" && (
            <div className="abx-flash-hint">Press <b>Start flashing</b> and compute the result in your head.</div>
          )}
          {flashing && (
            <div key={frameKey} className={`abx-flash-frame ${frameFontClass}`}>
              {frame == null ? (
                <span className="abx-flash-num is-blank">·</span>
              ) : (
                frame.items.map((it, i) => (
                  <span key={i} className={`abx-flash-num ${it.op === "−" ? "is-neg" : "is-pos"}`}>
                    {it.op ? `${it.op} ` : ""}{formatNumber(it.value)}
                  </span>
                ))
              )}
            </div>
          )}
          {phase === "answer" && (
            <form className="abx-answer-form" onSubmit={check}>
              <div className="abx-answer-q">What is the result?</div>
              <input ref={inputRef} type="number" inputMode="numeric" className="abx-answer-input"
                value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="Your answer" />
              <button type="submit" className="abx-btn abx-btn--primary">Check</button>
            </form>
          )}
          {phase === "result" && (
            <div className={`abx-verdict ${verdict}`}>
              <div className="abx-verdict-mark">{verdict === "right" ? "✓" : "✗"}</div>
              <div className="abx-verdict-title">{verdict === "right" ? "Correct!" : "Not quite"}</div>
              <div className="abx-verdict-detail">
                Answer: <b>{formatNumber(problem.answer)}</b>
                {verdict === "wrong" && <> · you said <b>{guess === "" ? "—" : formatNumber(Number(guess))}</b></>}
              </div>
              <div className="abx-verdict-seq">
                <span>{itemsToExpression(problem.items)}</span>
                <span className="eq">= {formatNumber(problem.answer)}</span>
              </div>
            </div>
          )}
        </div>

        {showBoard && (
          <div className="abx-mental-board">
            <AbacusBoard rods={boardRods} settings={settings} interactive={false} />
            {boardOverflow && <div className="abx-board-note">Result exceeds the abacus — showing last digits.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
